import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { studies as studiesApi } from '../utils/api';

/*
 * نمایشگر مطالعه — سه نما، جوی‌استیک، خط‌کش.
 *
 * **متن این کامپوننت انگلیسی است، برخلاف بقیه‌ی سامانه.** عمدی است: این
 * همان صفحه‌ای است که کنار گزارشِ انگلیسی دیده می‌شود و پزشک هر دو را با
 * هم می‌خواند. بقیه‌ی فلوترک فارسی می‌ماند.
 *
 * روی نسخه‌ی سبکی که سرور ساخته کار می‌کند، نه روی دایکام خام. هر محور یک
 * «اطلس» دارد — برش‌های کوچک‌شده چیده در چند تصویر بزرگ — پس جابه‌جایی
 * بین برش‌ها فقط یک drawImage است و به شبکه دست نمی‌زند.
 *
 * **مقیاس خط‌کش از مانیفستِ همان لایه و همان محور می‌آید.** اطلس
 * کوچک‌شده است و کرونال/ساجیتال نسبت ابعادشان با اگزیال فرق دارد؛ گرفتن
 * مقیاس از جای اشتباه یعنی عددِ غلط بدون هیچ نشانه‌ای.
 */

/*
 * از کجا فایل بگیرد.
 *
 * دو مسیر کاملاً متفاوت به این کامپوننت می‌رسند: کارمندِ واردشده (توکن
 * JWT) و پزشکی که فقط یک لینک دارد. به‌جای پاس دادن یک prop از میان چهار
 * لایه، منبع را از context می‌گیرد و پیش‌فرضش همان مسیر کارمندی است — پس
 * هیچ‌کدام از استفاده‌های موجود تغییری نمی‌خواهند.
 */
export const ViewerSource = createContext(studiesApi);

const AXES = ['axial', 'coronal', 'sagittal'];

/**
 * روی صفحه‌ی باریک فقط یک نما نشان داده می‌شود.
 *
 * ۹۹٪ پزشک‌ها تصویر را روی گوشی می‌بینند. سه نما کنار هم روی ۳۷۵ پیکسل
 * یعنی سه بندانگشتیِ ۱۱۰ پیکسلی — که نمایشگر نیست. پس روی موبایل یک نمای
 * تمام‌عرض با کلید تعویض محور، و روی دسکتاپ هر سه با هم.
 *
 * سود دومش هم کم نیست: اطلسِ محورهای دیگر تا وقتی انتخاب نشوند بارگذاری
 * نمی‌شوند. کرونال و ساجیتال هرکدام ۲۰ برگ دارند؛ گرفتن هر سه محور روی
 * اینترنت همراه یعنی ۴۳ درخواست پیش از دیدن اولین تصویر.
 */
function useNarrow() {
  const query = '(max-width: 900px)';
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (event) => setNarrow(event.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return narrow;
}
const AXIS_LABEL = { axial: 'Axial', coronal: 'Coronal', sagittal: 'Sagittal' };

// زرد پرکنتراست روی تصویر خاکستری — روی هر دو سر طیف خوانا می‌ماند.
const INK = '#FFD400';
const INK_SHADOW = 'rgba(0,0,0,0.85)';

function usePanImages(studyId, names, version) {
  const source = useContext(ViewerSource);
  const [images, setImages] = useState([]);
  const [loaded, setLoaded] = useState(0);

  useEffect(() => {
    if (!names || !names.length) return undefined;
    let alive = true;
    const revokers = [];
    setImages([]);
    setLoaded(0);

    (async () => {
      const out = [];
      for (const name of names) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const { image, revoke } = await source.renditionImage(studyId, name, version);
          if (!alive) { revoke(); return; }
          revokers.push(revoke);
          out.push(image);
          setImages([...out]);
          setLoaded(out.length);
        } catch { break; }
      }
    })();

    return () => {
      alive = false;
      revokers.forEach((r) => r());
    };
  }, [source, studyId, names, version]);

  return { images, ready: names ? loaded >= names.length : false, loaded };
}

/** یک نما: بوم، اطلس، خط‌کش، و ورودی لمسی. */
function Pane({ studyId, version, axis, spec, index, onIndex, tool, lines, onLine, active, onActivate }) {
  const source = useContext(ViewerSource);
  const canvasRef = useRef(null);
  const [full, setFull] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [pending, setPending] = useState(null);
  const pendingRef = useRef(null);

  const sheetNames = useMemo(() => spec.atlas.sheets, [spec]);
  const { images, ready, loaded } = usePanImages(studyId, sheetNames, version);

  // در بزرگ‌نمایی بالا، همان یک برش را تمام‌اندازه می‌گیریم.
  const wantFull = zoom > 1.35;
  useEffect(() => {
    if (!wantFull) { setFull(null); return undefined; }
    let alive = true;
    let revoke = null;
    const name = spec.full.pattern.replace('{:04d}', String(index).padStart(4, '0'));
    source.renditionImage(studyId, name, version)
      .then((r) => {
        if (!alive) { r.revoke(); return; }
        revoke = r.revoke;
        setFull(r.image);
      })
      .catch(() => alive && setFull(null));
    return () => { alive = false; if (revoke) revoke(); };
  }, [source, studyId, spec, index, wantFull, version]);

  // عوض کردن ابزار یا برش، نقطه‌ی نیمه‌کاره را بی‌معنی می‌کند.
  useEffect(() => {
    pendingRef.current = null;
    setPending(null);
  }, [tool, index]);

  const layer = full ? spec.full : spec.atlas;
  const srcW = full ? spec.width : spec.atlas.tile_w;
  const srcH = full ? spec.height : spec.atlas.tile_h;

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, W, H);

    // تصویر با حفظ نسبت ابعاد در بوم جا می‌شود.
    const fit = Math.min(W / srcW, H / srcH);
    const dw = srcW * fit * zoom;
    const dh = srcH * fit * zoom;
    const dx = (W - dw) / 2 + pan.x;
    const dy = (H - dh) / 2 + pan.y;

    const source = full || images[Math.floor(index / (spec.atlas.cols * spec.atlas.rows))];
    if (source) {
      if (full) {
        ctx.drawImage(source, dx, dy, dw, dh);
      } else {
        const per = spec.atlas.cols * spec.atlas.rows;
        const slot = index % per;
        ctx.drawImage(
          source,
          (slot % spec.atlas.cols) * spec.atlas.tile_w,
          Math.floor(slot / spec.atlas.cols) * spec.atlas.tile_h,
          spec.atlas.tile_w, spec.atlas.tile_h,
          dx, dy, dw, dh
        );
      }
    }

    // --- خط‌کش‌ها ---
    const toCanvas = (p) => [dx + (p[0] / srcW) * dw, dy + (p[1] / srcH) * dh];
    const mm = layer.pixel_spacing_mm;
    const all = pending ? [...lines, pending] : lines;

    ctx.lineWidth = 2;
    ctx.font = '600 15px system-ui, sans-serif';
    ctx.textBaseline = 'bottom';

    all.forEach((line) => {
      if (!line.a) return;
      const a = toCanvas(line.a);
      ctx.strokeStyle = INK;
      ctx.fillStyle = INK;

      if (line.b) {
        const b = toCanvas(line.b);
        ctx.beginPath();
        ctx.moveTo(a[0], a[1]);
        ctx.lineTo(b[0], b[1]);
        ctx.stroke();

        const dxm = (line.b[0] - line.a[0]) * mm[0];
        const dym = (line.b[1] - line.a[1]) * mm[1];
        const label = `${Math.sqrt(dxm * dxm + dym * dym).toFixed(1)} mm`;
        const mx = (a[0] + b[0]) / 2;
        const my = (a[1] + b[1]) / 2 - 8;

        // سایه‌ی تیره پشت متن: روی استخوانِ سفید هم خوانا بماند.
        ctx.strokeStyle = INK_SHADOW;
        ctx.lineWidth = 4;
        ctx.strokeText(label, mx, my);
        ctx.fillStyle = INK;
        ctx.fillText(label, mx, my);
        ctx.lineWidth = 2;
        ctx.strokeStyle = INK;
      }

      [line.a, line.b].filter(Boolean).forEach((p) => {
        const c = toCanvas(p);
        ctx.fillRect(c[0] - 3, c[1] - 3, 6, 6);
      });
    });
  }, [images, index, spec, full, zoom, pan, lines, pending, layer, srcW, srcH]);

  useEffect(draw, [draw]);

  // ------------------------------------------------------------ ورودی
  const pointers = useRef(new Map());
  const gesture = useRef(null);

  const toImage = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const W = canvas.width;
    const H = canvas.height;
    const fit = Math.min(W / srcW, H / srcH);
    const dw = srcW * fit * zoom;
    const dh = srcH * fit * zoom;
    const dx = (W - dw) / 2 + pan.x;
    const dy = (H - dh) / 2 + pan.y;
    const cx = ((event.clientX - rect.left) / rect.width) * W;
    const cy = ((event.clientY - rect.top) / rect.height) * H;
    return [((cx - dx) / dw) * srcW, ((cy - dy) / dh) * srcH];
  };

  const onPointerDown = (event) => {
    onActivate();
    // روی بعضی مرورگرها و ورودی‌های ساختگی پرتاب می‌کند. گرفتن اشاره‌گر
    // یک راحتی است، نه شرط کار — استثنایش نباید بقیه‌ی این تابع را
    // بی‌صدا از کار بیندازد.
    try {
      canvasRef.current.setPointerCapture(event.pointerId);
    } catch { /* بی‌اهمیت */ }

    pointers.current.set(event.pointerId, event);

    if (pointers.current.size === 2) {
      const [p, q] = [...pointers.current.values()];
      gesture.current = {
        kind: 'pinch',
        distance: Math.hypot(p.clientX - q.clientX, p.clientY - q.clientY),
        zoom,
      };
      return;
    }

    if (tool === 'measure') {
      // نقطه‌ی معلق در ref نگه داشته می‌شود، نه در state: دو کلیک پیاپی
      // در یک تیکِ رندر، مقدار state را کهنه می‌بینند و خط دوم هرگز
      // بسته نمی‌شود.
      const point = toImage(event);
      if (pendingRef.current) {
        onLine({ a: pendingRef.current, b: point });
        pendingRef.current = null;
        setPending(null);
      } else {
        pendingRef.current = point;
        setPending({ a: point });
      }
      return;
    }
    // یک انگشت بدون ابزار: اسکرول بین برش‌ها
    gesture.current = { kind: 'scrub', y: event.clientY, index };
  };

  const onPointerMove = (event) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, event);
    const g = gesture.current;
    if (!g) return;

    if (g.kind === 'pinch' && pointers.current.size === 2) {
      const [p, q] = [...pointers.current.values()];
      const distance = Math.hypot(p.clientX - q.clientX, p.clientY - q.clientY);
      setZoom(Math.min(6, Math.max(1, (g.zoom * distance) / g.distance)));
      return;
    }
    if (g.kind === 'scrub') {
      // حرکت عمودی انگشت = جابه‌جایی بین برش‌ها.
      const step = Math.round((event.clientY - g.y) / 4);
      onIndex(Math.min(spec.slices - 1, Math.max(0, g.index + step)));
    }
  };

  const onPointerUp = (event) => {
    pointers.current.delete(event.pointerId);
    if (pointers.current.size < 2) gesture.current = null;
  };

  const onWheel = (event) => {
    event.preventDefault();
    if (event.ctrlKey) {
      setZoom((z) => Math.min(6, Math.max(1, z - event.deltaY / 400)));
      return;
    }
    onIndex(Math.min(spec.slices - 1, Math.max(0, index + (event.deltaY > 0 ? 1 : -1))));
  };

  return (
    <div className={`pane ${active ? 'is-active' : ''}`}>
      <div className="pane__head">
        <span className="pane__title">{AXIS_LABEL[axis]}</span>
        <span className="pane__count">{index + 1} / {spec.slices}</span>
      </div>

      <canvas
        ref={canvasRef}
        width={420}
        height={420}
        className={`pane__canvas ${tool === 'measure' ? 'is-measuring' : ''}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onWheel={onWheel}
      />

      {!ready && (
        <p className="pane__hint">
          Loading {loaded}/{spec.atlas.sheets.length}
        </p>
      )}
      {zoom > 1.01 && (
        <button type="button" className="pane__reset" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}>
          Reset zoom ({zoom.toFixed(1)}×)
        </button>
      )}
    </div>
  );
}

/**
 * جوی‌استیک: کشیدن دسته، برش را جلو و عقب می‌برد.
 *
 * یک تایمرِ ثابت دارد که در هر تیک `offsetRef` را می‌خواند. نسخه‌ی اول
 * تایمر را در هر `pointermove` می‌کشت و از نو می‌ساخت — و چون موقع کشیدن
 * ده‌ها رویداد در ثانیه می‌آید، تایمر عملاً هیچ‌وقت به تیک نمی‌رسید و
 * سرعت به یک گام در ثانیه می‌افتاد.
 */
function Joystick({ onStep, label }) {
  const boxRef = useRef(null);
  const [offset, setOffset] = useState(0);
  const offsetRef = useRef(0);
  const timer = useRef(null);
  const stepRef = useRef(onStep);
  stepRef.current = onStep;

  const stop = useCallback(() => {
    clearInterval(timer.current);
    timer.current = null;
    offsetRef.current = 0;
    setOffset(0);
  }, []);

  const track = (event) => {
    const rect = boxRef.current.getBoundingClientRect();
    const centre = rect.top + rect.height / 2;
    const value = Math.max(-1, Math.min(1, (event.clientY - centre) / (rect.height / 2)));
    offsetRef.current = value;
    setOffset(value);
  };

  const start = (event) => {
    try {
      boxRef.current.setPointerCapture(event.pointerId);
    } catch { /* بی‌اهمیت */ }
    track(event);

    if (timer.current) return;
    // تیک ثابت و سریع؛ خودِ تیک تصمیم می‌گیرد این‌بار قدم بردارد یا نه.
    // سرعت با فاصله از مرکز زیاد می‌شود، مثل جوی‌استیک واقعی.
    let carry = 0;
    timer.current = setInterval(() => {
      const value = offsetRef.current;
      if (Math.abs(value) < 0.12) return;
      carry += Math.abs(value) ** 2 * 3;   // ۰ تا ۳ گام در هر تیک
      const steps = Math.floor(carry);
      if (!steps) return;
      carry -= steps;
      for (let i = 0; i < steps; i += 1) stepRef.current(value > 0 ? 1 : -1);
    }, 30);
  };

  const move = (event) => {
    if (!timer.current) return;
    track(event);
  };

  useEffect(() => () => clearInterval(timer.current), []);

  return (
    <div className="joystick">
      <span className="joystick__label">{label}</span>
      <div
        ref={boxRef}
        className="joystick__track"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={stop}
        onPointerCancel={stop}
        // `pointerleave` نه: با گرفتن اشاره‌گر نباید بیاید، ولی اگر گرفتن
        // شکست خورده باشد، بیرون رفتن انگشت از روی دسته وسط کشیدن،
        // جوی‌استیک را می‌کشت. `lostpointercapture` رویدادِ درستِ
        // «درگ به هر دلیلی تمام شد» است.
        onLostPointerCapture={stop}
      >
        <span className="joystick__knob" style={{ transform: `translateY(${offset * 40}px)` }} />
      </div>
    </div>
  );
}

export default function StudyViewer({ studyId, onClose }) {
  const source = useContext(ViewerSource);
  const [manifest, setManifest] = useState(null);
  const [error, setError] = useState('');
  const [seriesIndex, setSeriesIndex] = useState(0);
  const [indices, setIndices] = useState({ axial: 0, coronal: 0, sagittal: 0 });
  const [tool, setTool] = useState('scroll');
  const [lines, setLines] = useState({ axial: [], coronal: [], sagittal: [] });
  const [activeAxis, setActiveAxis] = useState('axial');

  const narrow = useNarrow();
  const series = manifest?.series?.[seriesIndex] || null;

  useEffect(() => {
    let alive = true;
    source.manifest(studyId)
      .then(({ data }) => alive && setManifest(data))
      .catch((err) => alive && setError(
        err?.response?.status === 404
          ? 'The viewer version of this study has not been built yet.'
          : 'Could not load the study.'
      ));
    return () => { alive = false; };
  }, [source, studyId]);

  // وقتی سری عوض شد، وسطِ هر محور نقطه‌ی شروع بهتری از صفر است.
  useEffect(() => {
    if (!series) return;
    setIndices({
      axial: Math.floor(series.axes.axial.slices / 2),
      coronal: Math.floor(series.axes.coronal.slices / 2),
      sagittal: Math.floor(series.axes.sagittal.slices / 2),
    });
    setLines({ axial: [], coronal: [], sagittal: [] });
  }, [series]);

  if (error) return <p className="alert alert--danger">{error}</p>;
  if (!manifest || !series) return <p className="studies__empty">Loading viewer…</p>;

  const setIndex = (axis, value) => setIndices((prev) => ({ ...prev, [axis]: value }));
  const addLine = (axis, line) =>
    setLines((prev) => ({ ...prev, [axis]: [...prev[axis], line] }));
  const totalLines = AXES.reduce((n, a) => n + lines[a].length, 0);

  return (
    <div className="viewer" dir="ltr">
      <div className="viewer__bar">
        {manifest.series.length > 1 && (
          <select
            className="input"
            value={seriesIndex}
            onChange={(event) => setSeriesIndex(Number(event.target.value))}
          >
            {manifest.series.map((s, i) => (
              <option key={s.uid} value={i}>
                {s.description || 'Series'} — {s.depth} slices
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          className={`btn btn--sm ${tool === 'scroll' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setTool('scroll')}
        >
          Scroll
        </button>
        <button
          type="button"
          className={`btn btn--sm ${tool === 'measure' ? 'btn--primary' : 'btn--ghost'}`}
          onClick={() => setTool('measure')}
        >
          Measure
        </button>
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={!totalLines}
          onClick={() => setLines({ axial: [], coronal: [], sagittal: [] })}
        >
          Clear ({totalLines})
        </button>

        {onClose && (
          <button type="button" className="btn btn--ghost btn--sm viewer__close" onClick={onClose}>
            Close
          </button>
        )}
      </div>

      {narrow && (
        <div className="viewer__axes" role="tablist">
          {AXES.map((axis) => (
            <button
              key={axis}
              type="button"
              role="tab"
              aria-selected={activeAxis === axis}
              className={`viewer__axis ${activeAxis === axis ? 'is-active' : ''}`}
              onClick={() => setActiveAxis(axis)}
            >
              {AXIS_LABEL[axis]}
            </button>
          ))}
        </div>
      )}

      <div className={`viewer__panes ${narrow ? 'is-single' : ''}`}>
        {(narrow ? [activeAxis] : AXES).map((axis) => (
          <Pane
            key={axis}
            studyId={studyId}
            version={manifest.version}
            axis={axis}
            spec={series.axes[axis]}
            index={indices[axis]}
            onIndex={(value) => setIndex(axis, value)}
            tool={tool}
            lines={lines[axis]}
            onLine={(line) => addLine(axis, line)}
            active={activeAxis === axis}
            onActivate={() => setActiveAxis(axis)}
          />
        ))}
      </div>

      <div className="viewer__controls">
        <Joystick
          label={AXIS_LABEL[activeAxis]}
          onStep={(step) =>
            setIndices((prev) => ({
              ...prev,
              [activeAxis]: Math.min(
                series.axes[activeAxis].slices - 1,
                Math.max(0, prev[activeAxis] + step)
              ),
            }))
          }
        />
        <div className="viewer__scrub">
          <input
            type="range"
            min={0}
            max={Math.max(0, series.axes[activeAxis].slices - 1)}
            value={indices[activeAxis]}
            onChange={(event) => setIndex(activeAxis, Number(event.target.value))}
          />
          <span className="viewer__count">
            {AXIS_LABEL[activeAxis]} {indices[activeAxis] + 1} / {series.axes[activeAxis].slices}
          </span>
        </div>
      </div>

      <p className="viewer__caveat">{manifest.note}</p>
    </div>
  );
}
